import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchCollection } from '../lib/cms';
import { getOptimizedImageUrl } from '../lib/images';
import GallerySection from '../components/GallerySection';

export default function ServicesPage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Ydelser & Online SoMe-forløb - Health4media.dk';
    let isMounted = true;

    async function loadServices() {
      try {
        const data = await fetchCollection('services');
        if (isMounted) setServices(data || []);
      } catch (err) {
        console.error('Error loading services:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    loadServices();
    return () => { isMounted = false; };
  }, []);

  const valuePoints = [
    {
      title: 'Opbygge faglig troværdighed',
      desc: 'Formidl klinikkens specialistviden og erfaring i et sobert, etisk og troværdigt visuelt format.',
      icon: 'fa-certificate'
    },
    {
      title: 'Gøre ekspertviden let at forstå',
      desc: 'Omsæt komplicerede faglige begreber, diagnoser og procedurer til korte, pædagogiske videoer.',
      icon: 'fa-brain'
    },
    {
      title: 'Skabe større organisk synlighed',
      desc: 'Nå ud til tusindvis af lokale borgere og potentielle patienter gennem sociale mediers algoritmer.',
      icon: 'fa-arrow-trend-up'
    },
    {
      title: 'Gøre klinikken mere menneskelig',
      desc: 'Vis personerne bag behandlerkitlerne, så patienterne oplever varme, empati og imødekommenhed.',
      icon: 'fa-hands-holding-child'
    },
    {
      title: 'Skabe stærk genkendelighed',
      desc: 'Opbyg et genkendeligt brand i sundhedssektoren, så jeres klinik er førstevalget, når behovet opstår.',
      icon: 'fa-fingerprint'
    },
    {
      title: 'Øge tilliden inden første kontakt',
      desc: 'Fjern usikkerhed og frygt hos patienten, så henvendelsen føles tryg og ubesværet.',
      icon: 'fa-shield-heart'
    },
    {
      title: 'Generere flere relevante henvendelser',
      desc: 'Tiltræk motiverede patienter, der passer præcist til jeres specifikke behandlingskompetencer.',
      icon: 'fa-user-check'
    }
  ];

  return (
    <div className="bg-slate-50 min-h-screen">
      
      {/* HEADER HERO */}
      <section className="bg-slate-900 text-white py-10 sm:py-16 md:py-24 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-4">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-sky-500/20 border border-sky-400/30 text-sky-300 text-xs sm:text-sm font-semibold tracking-wide backdrop-blur-xs">
              <i className="fa-solid fa-shield-halved text-xs" aria-hidden="true" />
              <span>0 måneders binding · Fleksible aftaler</span>
            </div>

            <h1 className="text-2xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-white leading-tight">
              Professionelt content, der skaber tillid, synlighed & flere patienter
            </h1>

            <p className="text-sm sm:text-lg md:text-xl text-slate-300 leading-relaxed">
              Vi hjælper sundhedsklinikker og behandlere med at omsætte faglig ekspertise til engagerende, tryghedsskabende videoer. Få professionel optagevejledning, videoredigering, SoMe-profilopsætning og Meta postkalender, der opbygger stærk patienttillid og sikrer en stabil tilgang af nye henvendelser.
            </p>

            <div className="pt-2 flex flex-wrap items-center gap-4">
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 px-6 py-3 sm:px-7 sm:py-3.5 rounded-full bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold text-sm sm:text-base transition shadow-lg shadow-sky-950/40"
              >
                <span>Book en samtale</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </Link>
            </div>

            <div className="pt-2 flex flex-wrap items-center gap-y-2 gap-x-5 text-xs sm:text-sm text-slate-300 font-medium">
              <span className="flex items-center gap-1.5">
                <i className="fa-solid fa-check text-sky-400" aria-hidden="true" />
                <span>Professionelt shortform-content</span>
              </span>
              <span className="flex items-center gap-1.5">
                <i className="fa-solid fa-check text-sky-400" aria-hidden="true" />
                <span>Målrettet synlighed i lokalområdet</span>
              </span>
              <span className="flex items-center gap-1.5">
                <i className="fa-solid fa-check text-sky-400" aria-hidden="true" />
                <span>Skræddersyet til jeres praksis</span>
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* CORE VALUE & EFFECT FOR HEALTHCARE (7 Key Impacts) */}
      <section className="py-16 sm:py-24 bg-white border-b border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              Hvad kan professionel videoproduktion og formidling gøre for jeres praksis?
            </h2>
            <p className="text-base sm:text-lg text-slate-600 mt-3">
              Visuel formidling er det mest effektive redskab for sundhedsvirksomheder til at opbygge patienttillid og fjerne tvivl før første konsultation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {valuePoints.map((pt, idx) => (
              <div
                key={idx}
                className="bg-slate-50 p-6 sm:p-7 rounded-2xl border border-slate-200/80 flex flex-col justify-between"
              >
                <div>
                  <div className="text-2xl text-sky-600 mb-3">
                    <i className={`fa-solid ${pt.icon}`} aria-hidden="true" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">
                    {pt.title}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {pt.desc}
                  </p>
                </div>
              </div>
            ))}

            {/* CTA card as last box */}
            <div className="bg-slate-900 text-white p-6 sm:p-7 rounded-2xl border border-slate-800 flex flex-col justify-between">
              <div>
                <span className="text-xs font-bold text-sky-400 uppercase tracking-wider">
                  Individuel løsning
                </span>
                <h3 className="text-xl font-bold text-white mt-2 mb-2">
                  Tilpasset jeres klinik
                </h3>
                <p className="text-sm text-slate-300 leading-relaxed">
                  Vi sammensætter produktion og frekvens ud fra jeres faglige mål og behov.
                </p>
              </div>
              <div className="mt-6">
                <Link
                  to="/kontakt"
                  className="w-full inline-flex items-center justify-center gap-2 py-3 px-4 rounded-full bg-sky-500 hover:bg-sky-400 text-slate-950 font-semibold text-sm transition"
                >
                  <span>Book en samtale</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* DETAILED SERVICES LIST (FROM CMS) */}
      <section className="py-16 sm:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="max-w-3xl mx-auto text-center mb-16">
            <span className="text-xs font-bold text-sky-700 uppercase tracking-wider block mb-2">
              Vores Ydelser
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              En bred vifte af services – sammensat til jeres behov
            </h2>
            <p className="text-base sm:text-lg text-slate-600 mt-3">
              Klinikker har forskellige forudsætninger og behov. Derfor tilbyder vi en bred vifte af fleksible ydelser, som I kan kombinere præcist efter jeres ønsker – helt uden faste pakker og altid med 0 binding.
            </p>
          </div>

          <div className="space-y-12">
            {services.map((srv, idx) => {
              const imgUrl = srv.featured_image?.[0] || '';
              const isEven = idx % 2 === 1;
              const sectionId = srv.identifier === 'klinisk-videoproduktion' 
                ? 'produktion' 
                : srv.identifier === 'distribution-og-synlighed' 
                  ? 'distribution' 
                  : undefined;

              return (
                <div
                  key={srv.id || srv.identifier}
                  id={sectionId}
                  className={`bg-white rounded-3xl p-8 sm:p-12 border border-slate-200/90 shadow-xs flex flex-col lg:flex-row gap-10 items-center ${
                    isEven ? 'lg:flex-row-reverse' : ''
                  }`}
                >
                  {/* Visual */}
                  <div className="w-full lg:w-1/2 shrink-0">
                    <div className="relative overflow-hidden aspect-4/3 bg-slate-100 border border-slate-200">
                      {imgUrl && (
                        <img
                          src={getOptimizedImageUrl(imgUrl, { width: 800, height: 600, fit: 'cover' })}
                          alt={srv.title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      )}
                    </div>
                  </div>

                  {/* Text Content */}
                  <div className="w-full lg:w-1/2 space-y-4">
                    <span className="text-xs font-bold text-sky-700 uppercase tracking-wider">
                      Fokuseret ydelse
                    </span>
                    <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
                      {srv.title}
                    </h3>
                    <p className="text-base text-slate-600 leading-relaxed font-medium">
                      {srv.short_description}
                    </p>

                    {/* Rich text content if available */}
                    {srv.full_content && (
                      <div 
                        className="text-sm text-slate-600 leading-relaxed space-y-3 pt-2 prose prose-slate max-w-none"
                        dangerouslySetInnerHTML={{ __html: srv.full_content }}
                      />
                    )}

                    {srv.key_benefits && (
                      <div className="pt-4 border-t border-slate-100 flex flex-wrap gap-2">
                        {srv.key_benefits.split(',').map((b, bi) => (
                          <span
                            key={bi}
                            className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700 bg-slate-100 px-3 py-1.5 rounded-lg"
                          >
                            <i className="fa-solid fa-check text-sky-600 text-[10px]" aria-hidden="true" />
                            <span>{b.trim()}</span>
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="pt-4 flex flex-wrap items-center gap-4">
                      <Link
                        to={
                          srv.identifier === 'shortform-content'
                            ? '/ydelser/shortform-content'
                            : srv.identifier === 'klinisk-videoproduktion'
                              ? '/ydelser/klinisk-videoproduktion'
                              : '/ydelser/distribution-og-synlighed'
                        }
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-sky-50 hover:bg-sky-100 text-sky-700 font-bold text-sm transition border border-sky-200"
                      >
                        <span>Læs mere om denne ydelse</span>
                        <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                      </Link>
                      <Link
                        to="/kontakt"
                        className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900 transition py-2"
                      >
                        <span>Book en samtale</span>
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* PALETTE OF ALL SERVICES (Full Breakdown Grid) */}
          <div className="mt-20 pt-16 border-t border-slate-200">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <span className="text-xs font-bold text-sky-700 uppercase tracking-wider block mb-2">
                Oversigt over kompetencer
              </span>
              <h3 className="text-2xl sm:text-3xl font-bold text-slate-900">
                Hele paletten af services, vi tilbyder klinikken
              </h3>
              <p className="text-sm sm:text-base text-slate-600 mt-2">
                I kan vælge enkelte ydelser eller lade os varetage hele kæden fra idé til færdig udgivelse.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              
              <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-2xs hover:shadow-md transition">
                <div className="w-12 h-12 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center text-xl mb-4">
                  <i className="fa-solid fa-mobile-screen-button" aria-hidden="true" />
                </div>
                <h4 className="text-base font-bold text-slate-900 mb-2">
                  1. Optagevejledning til mobilen
                </h4>
                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                  Trin-for-trin online vejledning til at optage roligt og professionelt med smartphone i klinikken – vinkler, lys, mikrofon og kropssprog.
                </p>
              </div>

              <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-2xs hover:shadow-md transition">
                <div className="w-12 h-12 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center text-xl mb-4">
                  <i className="fa-solid fa-camera-retro" aria-hidden="true" />
                </div>
                <h4 className="text-base font-bold text-slate-900 mb-2">
                  2. Billedtagning & Fotovejledning
                </h4>
                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                  Rådgivning til autentiske billeder af behandlere, klinikmiljø, konsultationsrum og hverdagsglimt til jeres profiler og opslag.
                </p>
              </div>

              <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-2xs hover:shadow-md transition">
                <div className="w-12 h-12 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center text-xl mb-4">
                  <i className="fa-solid fa-scissors" aria-hidden="true" />
                </div>
                <h4 className="text-base font-bold text-slate-900 mb-2">
                  3. Professionel Videoredigering
                </h4>
                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                  Klipning, dynamisk pacing, fjernelse af pauser og justering til 9:16 formater, der fastholder seeren på Reels, TikTok & Shorts.
                </p>
              </div>

              <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-2xs hover:shadow-md transition">
                <div className="w-12 h-12 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center text-xl mb-4">
                  <i className="fa-solid fa-closed-captioning" aria-hidden="true" />
                </div>
                <h4 className="text-base font-bold text-slate-900 mb-2">
                  4. Dynamiske Undertekster & Grafik
                </h4>
                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                  Tydelige undertekster (vigtigt da 80% ser uden lyd), kliniklogo, farver og grafiske elementer, der styrker genkendeligheden.
                </p>
              </div>

              <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-2xs hover:shadow-md transition">
                <div className="w-12 h-12 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center text-xl mb-4">
                  <i className="fa-solid fa-calendar-days" aria-hidden="true" />
                </div>
                <h4 className="text-base font-bold text-slate-900 mb-2">
                  5. Meta SoMe Postkalender
                </h4>
                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                  Oprettelse og løbende drift af en struktureret udgivelsesplan, så klinikken opnår en stabil og troværdig tilstedeværelse.
                </p>
              </div>

              <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-2xs hover:shadow-md transition">
                <div className="w-12 h-12 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center text-xl mb-4">
                  <i className="fa-solid fa-users-gear" aria-hidden="true" />
                </div>
                <h4 className="text-base font-bold text-slate-900 mb-2">
                  6. SoMe-profilopsætning fra bunden
                </h4>
                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                  Oprettelse og fuld optimering af profiler på Instagram, Facebook, TikTok m.fl. – inklusiv bio-tekst, kategorier og visuel profil.
                </p>
              </div>

              <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-2xs hover:shadow-md transition">
                <div className="w-12 h-12 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center text-xl mb-4">
                  <i className="fa-solid fa-lightbulb" aria-hidden="true" />
                </div>
                <h4 className="text-base font-bold text-slate-900 mb-2">
                  7. Idébank & Emnesparring
                </h4>
                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                  Konkret sparring om hvad I skal tale om: afmystificering af diagnoser, besvarelse af patienters frygt og faglige emneidéer.
                </p>
              </div>

              <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-2xs hover:shadow-md transition">
                <div className="w-12 h-12 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center text-xl mb-4">
                  <i className="fa-solid fa-bullseye" aria-hidden="true" />
                </div>
                <h4 className="text-base font-bold text-slate-900 mb-2">
                  8. Lokal Rækkevidde & Synlighed
                </h4>
                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                  Målrettet synlighed over for borgere og potentielle patienter i jeres by og optageområde, så klinikken er top-of-mind.
                </p>
              </div>

            </div>
          </div>

        </div>
      </section>

      {/* PROCESS TIMELINE */}
      <section className="py-16 sm:py-24 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="max-w-3xl mx-auto text-center mb-16">
            <span className="text-xs font-bold text-sky-700 uppercase tracking-wider block mb-2">
              Trin for trin
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              Sådan forløber samarbejdet
            </h2>
            <p className="text-base sm:text-lg text-slate-600 mt-3">
              Fra den første indledende samtale til jeres færdige videoer er udgivet via postkalenderen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            
            <div className="border-l-2 border-sky-500 pl-4 py-2">
              <span className="text-xs font-bold text-sky-600 uppercase tracking-wider">Trin 1</span>
              <h4 className="text-lg font-bold text-slate-900 mt-1">Online afklaring</h4>
              <p className="text-xs sm:text-sm text-slate-600 mt-2 leading-relaxed">
                Vi afstemmer klinikkens målgruppe, faglige emner og de hyppigste patienthenvendelser.
              </p>
            </div>

            <div className="border-l-2 border-sky-500 pl-4 py-2">
              <span className="text-xs font-bold text-sky-600 uppercase tracking-wider">Trin 2</span>
              <h4 className="text-lg font-bold text-slate-900 mt-1">Vejledning til optagelse</h4>
              <p className="text-xs sm:text-sm text-slate-600 mt-2 leading-relaxed">
                Vi rådgiver jer online om gode vinkler, lys, mikrofon og opbygning af korte optagelser med mobilen.
              </p>
            </div>

            <div className="border-l-2 border-sky-500 pl-4 py-2">
              <span className="text-xs font-bold text-sky-600 uppercase tracking-wider">Trin 3</span>
              <h4 className="text-lg font-bold text-slate-900 mt-1">I optager & sender materialet</h4>
              <p className="text-xs sm:text-sm text-slate-600 mt-2 leading-relaxed">
                I filmer i klinikken i eget tempo uden forstyrrelser af patientflowet og sender råfilerne online til os.
              </p>
            </div>

            <div className="border-l-2 border-sky-500 pl-4 py-2">
              <span className="text-xs font-bold text-sky-600 uppercase tracking-wider">Trin 4</span>
              <h4 className="text-lg font-bold text-slate-900 mt-1">Redigering & Meta postkalender</h4>
              <p className="text-xs sm:text-sm text-slate-600 mt-2 leading-relaxed">
                Vi klipper videoerne, undertekster og planlægger udgivelsen i en struktureret Meta SoMe kalender.
              </p>
            </div>

          </div>

          {/* CUSTOMIZED PARTNERSHIP BOX */}
          <div className="mt-16 bg-gradient-to-br from-slate-900 to-slate-950 text-white rounded-3xl p-8 sm:p-12 border border-slate-800 shadow-xl">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-7 space-y-4">
                <span className="text-xs font-bold text-sky-400 uppercase tracking-wider block">
                  Individuel tilpasning
                </span>
                <h3 className="text-2xl sm:text-3xl font-extrabold text-white">
                  En løsning sammensat til jeres praksis
                </h3>
                <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
                  Hver klinik har sine egne behandlingsformer, patienttyper og behov. Nogle ønsker fuld opstart med nye profiler og ugentlig redigering, mens andre har brug for løbende sparring og planlægning af udgivelser.
                </p>
                <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
                  Vi tager altid udgangspunkt i jeres situation og ambitioner, så I får præcis den støtte, der gør den største forskel for jeres synlighed og hverdag.
                </p>
              </div>

              <div className="lg:col-span-5 bg-slate-800/80 rounded-2xl p-6 sm:p-8 border border-slate-700/80 text-left space-y-4">
                <div className="w-10 h-10 rounded-xl bg-sky-500/20 text-sky-400 flex items-center justify-center font-bold text-lg">
                  <i className="fa-solid fa-comments" aria-hidden="true" />
                </div>
                <h4 className="text-xl font-bold text-white leading-snug">
                  Tag en uforpligtende snak om klinikkens muligheder
                </h4>
                <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                  Vi gennemgår jeres ønsker og skitserer, hvordan vi bedst kan styrke jeres digitale profil og skabe tryghed hos nye patienter.
                </p>
                <div className="pt-2">
                  <Link
                    to="/kontakt"
                    className="w-full inline-flex items-center justify-center gap-2 py-3.5 px-6 rounded-full bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold text-sm transition shadow-md"
                  >
                    <span>Book en samtale</span>
                    <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-14 text-center">
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-slate-900 text-white hover:bg-sky-700 font-bold transition shadow-md"
            >
              <span>Book en samtale om jeres forløb</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>

        </div>
      </section>

      {/* CMS GALLERI SEKTION */}
      <GallerySection
        gallerySlug="produktion-i-klinikken"
        title="Optagelser og formater fra klinikker"
        subtitle="Se eksempler på autentisk og professionelt videoarbejde."
        className="bg-slate-50"
      />

    </div>
  );
}
